import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useImportarExcelLab } from '@/hooks/useLaboratorio';
import { FileUp, Loader2, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Modal para importar el Excel histórico de laboratorio
 * (PRUEBAS RESISTENCIA A LA COMPRESIÓN DEL CONCRETO YYYY.xlsx).
 *
 * Permite subir el archivo directamente desde OneDrive/disco local. El
 * backend lee la hoja "BASE DATOS" y reconcilia cada fila con las muestras
 * existentes matcheando por (fecha de colado + casa/obra):
 *   - Si la muestra ya existe → le carga los resultados (MPa) que falten,
 *     sin pisar los que ya estaban.
 *   - Si no existe → la crea con sus ensayos 7/14/28d.
 * Es idempotente: subir el mismo Excel dos veces no duplica nada.
 *
 * Tamaño máximo: 20 MB (suficiente para varios años de histórico).
 */

interface Props {
  abierto: boolean;
  cerrar: () => void;
}

const TAM_MAX = 20 * 1024 * 1024; // 20 MB

export function ModalImportarExcelLab({ abierto, cerrar }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [archivoElegido, setArchivoElegido] = useState<File | null>(null);
  const importar = useImportarExcelLab();

  const validar = (archivo: File): boolean => {
    if (!archivo.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Solo se aceptan archivos .xlsx', {
        description: `Recibido: ${archivo.name}`,
      });
      return false;
    }
    if (archivo.size > TAM_MAX) {
      const mb = Math.round(archivo.size / 1024 / 1024);
      toast.error(`Archivo demasiado grande (${mb} MB). Máximo 20 MB.`);
      return false;
    }
    return true;
  };

  const onSeleccionarArchivo = (archivo: File) => {
    if (validar(archivo)) setArchivoElegido(archivo);
  };

  const onSubir = () => {
    if (!archivoElegido) return;
    importar.mutate(archivoElegido, {
      onSuccess: (r) => {
        if (r.data) {
          setArchivoElegido(null);
          cerrar();
        }
      },
    });
  };

  return (
    <Dialog
      open={abierto}
      onOpenChange={(o) => {
        if (!o && !importar.isPending) {
          setArchivoElegido(null);
          cerrar();
        }
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Importar Excel histórico</DialogTitle>
          <DialogDescription>
            Sube el archivo{' '}
            <span className="font-mono text-xs">
              PRUEBAS RESISTENCIA A LA COMPRESIÓN DEL CONCRETO YYYY.xlsx
            </span>{' '}
            que está en OneDrive. El sistema lee la hoja{' '}
            <span className="font-mono text-xs">BASE DATOS</span> y reconcilia por{' '}
            <strong>fecha de colado + casa</strong>: carga los resultados (MPa) que falten a las
            muestras existentes y crea las que no estén. No pisa resultados ya cargados y se puede
            repetir sin duplicar.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setArrastrando(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setArrastrando(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setArrastrando(false);
            const archivo = e.dataTransfer.files?.[0];
            if (archivo) onSeleccionarArchivo(archivo);
          }}
          className={`flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed p-8 transition-colors ${
            arrastrando
              ? 'border-adelante-primary bg-adelante-primary/5'
              : 'border-border bg-muted/30'
          }`}
        >
          <FileUp className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
          {archivoElegido ? (
            <div className="text-center">
              <p className="text-sm font-medium">{archivoElegido.name}</p>
              <p className="text-xs text-muted-foreground">
                {(archivoElegido.size / 1024).toFixed(0)} KB · Listo para subir
              </p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm font-medium">Arrastrá el .xlsx o seleccionalo</p>
              <p className="text-xs text-muted-foreground">Máximo 20 MB</p>
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onSeleccionarArchivo(f);
              e.target.value = '';
            }}
          />

          <Button
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={importar.isPending}
          >
            {archivoElegido ? 'Cambiar archivo' : 'Seleccionar archivo'}
          </Button>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setArchivoElegido(null);
              cerrar();
            }}
            disabled={importar.isPending}
          >
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={onSubir}
            disabled={!archivoElegido || importar.isPending}
            className="gap-1.5"
          >
            {importar.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Importando…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" /> Importar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
